import type {
  NtAttendance, NtAttendanceStatus, NtAttendanceStored, NtBlock, NtCourse, NtDay, NtRoster, NtTerm,
} from '../../core/types'

/**
 * Attendance — the vocabulary, and the office's reports. Pure, so it is tested
 * (tests/attendanceLogic.test.ts).
 *
 * A course's attendance for a day is one document holding every rostered
 * student's mark, so "taken" is simply "the document exists". Morning comes
 * before afternoon everywhere, because that is the order the day happens in
 * and the order the office chases it in.
 */

export const STATUSES: Array<{ k: NtAttendanceStatus; label: string; short: string; tone: 'good' | 'critical' | 'serious' }> = [
  { k: 'present', label: 'Present', short: 'P', tone: 'good' },
  { k: 'absent', label: 'Absent', short: 'A', tone: 'critical' },
  { k: 'tardy-excused', label: 'Tardy, excused', short: 'TE', tone: 'serious' },
  { k: 'tardy-unexcused', label: 'Tardy, unexcused', short: 'TU', tone: 'serious' },
]
export const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.k, s.label])) as Record<NtAttendanceStatus, string>

/** Older records said `late` and `excused`; read them as the nearest current mark. */
export function normalizeStatus(s: NtAttendanceStored | undefined): NtAttendanceStatus | undefined {
  if (s === 'late') return 'tardy-unexcused'
  if (s === 'excused') return 'tardy-excused'
  return s
}

const BLOCK_ORDER: Record<NtBlock, number> = { AM: 0, PM: 1 }
const byBlockThenTitle = (a: { block: NtBlock | null; title: string }, b: { block: NtBlock | null; title: string }) =>
  (BLOCK_ORDER[a.block ?? 'AM'] - BLOCK_ORDER[b.block ?? 'AM']) || a.title.localeCompare(b.title)

/** Courses that actually meet: approved and in a block. */
const live = (courses: NtCourse[]) => courses.filter((c) => c.status === 'approved' && c.block)

const docFor = (att: NtAttendance[], courseId: string, day: NtDay) =>
  att.find((a) => a.courseId === courseId && a.day === day)

/**
 * Friday (day 3) is artifact and open-house prep: each student is with the
 * course they make their artifact in, so a course expects only those. Every
 * other day it expects its whole roster. Without the map, everyone counts.
 */
export type ArtifactOf = Map<string, NtBlock | null | undefined>
export const FRIDAY = 3
function expected(c: NtCourse, roster: NtRoster | undefined, day: NtDay, artifactOf?: ArtifactOf) {
  const all = roster?.students ?? []
  if (day !== FRIDAY || !artifactOf) return all
  return all.filter((s) => artifactOf.get(s.studentId) === c.block)
}

/**
 * Which day of the week it is, 0–3, or null outside the week. Drives what
 * counts as "today" for alerts.
 */
export function termDay(term: Pick<NtTerm, 'startDate'>, now: Date = new Date()): NtDay | null {
  if (!term.startDate) return null
  const start = new Date(term.startDate + 'T00:00:00')
  const d = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - start.getTime()) / 86_400_000)
  return d >= 0 && d <= 3 ? (d as NtDay) : null
}

/* ---------------- not taken ---------------- */

export interface CourseRef { courseId: string; title: string; block: NtBlock | null; teacherName: string; room: string }

const ref = (c: NtCourse): CourseRef => ({ courseId: c.id, title: c.title, block: c.block, teacherName: c.teacherName, room: c.room })

/** Courses with no attendance for the day, morning first. Only courses with students on them. */
export function notTaken(courses: NtCourse[], rosters: NtRoster[], att: NtAttendance[], day: NtDay, artifactOf?: ArtifactOf): CourseRef[] {
  return live(courses)
    .filter((c) => expected(c, rosters.find((r) => r.courseId === c.id), day, artifactOf).length > 0)
    .filter((c) => !docFor(att, c.id, day))
    .map(ref)
    .sort(byBlockThenTitle)
}

/* ---------------- who was not present ---------------- */

export interface MarkRow extends CourseRef {
  studentId: string
  name: string
  grade: number
  status: NtAttendanceStatus
  note: string
}

/** Everyone not marked present for the day — absent first, then tardy; morning before afternoon. */
export function exceptions(courses: NtCourse[], rosters: NtRoster[], att: NtAttendance[], day: NtDay, artifactOf?: ArtifactOf): MarkRow[] {
  const out: MarkRow[] = []
  for (const c of live(courses)) {
    const a = docFor(att, c.id, day)
    if (!a) continue
    const roster = rosters.find((r) => r.courseId === c.id)
    for (const st of expected(c, roster, day, artifactOf)) {
      const status = normalizeStatus(a.marks[st.studentId]) ?? 'present'
      if (status === 'present') continue
      out.push({ ...ref(c), studentId: st.studentId, name: st.name, grade: st.grade, status, note: a.notes?.[st.studentId] ?? '' })
    }
  }
  const rank: Record<NtAttendanceStatus, number> = { absent: 0, 'tardy-unexcused': 1, 'tardy-excused': 2, present: 3 }
  return out.sort((x, y) => BLOCK_ORDER[x.block ?? 'AM'] - BLOCK_ORDER[y.block ?? 'AM']
    || rank[x.status] - rank[y.status] || x.name.localeCompare(y.name))
}

/* ---------------- by course ---------------- */

export interface CourseDay extends CourseRef {
  enrolled: number
  taken: boolean
  takenBy?: string
  counts: Record<NtAttendanceStatus, number>
}

export function courseDay(courses: NtCourse[], rosters: NtRoster[], att: NtAttendance[], day: NtDay, artifactOf?: ArtifactOf): CourseDay[] {
  return live(courses).map((c) => {
    const who = expected(c, rosters.find((r) => r.courseId === c.id), day, artifactOf)
    const a = docFor(att, c.id, day)
    const counts = { present: 0, absent: 0, 'tardy-excused': 0, 'tardy-unexcused': 0 }
    if (a) for (const st of who) counts[normalizeStatus(a.marks[st.studentId]) ?? 'present']++
    return { ...ref(c), enrolled: who.length, taken: !!a, takenBy: a?.takenBy, counts }
  }).sort(byBlockThenTitle)
}

/* ---------------- one student ---------------- */

export interface StudentCell { course: CourseRef | null; status: NtAttendanceStatus | null; note: string }

/**
 * One student's week: four days × morning/afternoon. `course` is null where
 * they have no course in that block (an independent project); `status` null
 * where it has not been taken yet.
 */
export function studentWeek(studentId: string, courses: NtCourse[], rosters: NtRoster[], att: NtAttendance[], artifactOf?: ArtifactOf) {
  const theirs = live(courses).filter((c) => rosters.find((r) => r.courseId === c.id)?.students.some((s) => s.studentId === studentId))
  const cell = (block: NtBlock, day: NtDay): StudentCell => {
    // Friday: the morning cell is their artifact course, whichever block it
    // runs in; the rest of Friday (the open house, March Madness) takes no attendance.
    if (day === FRIDAY && artifactOf) {
      if (block === 'PM') return { course: null, status: null, note: '' }
      const ab = artifactOf.get(studentId)
      const c = ab ? theirs.find((x) => x.block === ab) : undefined
      if (!c) return { course: null, status: null, note: '' }
      const a = docFor(att, c.id, day)
      return { course: ref(c), status: a ? normalizeStatus(a.marks[studentId]) ?? 'present' : null, note: a?.notes?.[studentId] ?? '' }
    }
    const c = theirs.find((x) => x.block === block)
    if (!c) return { course: null, status: null, note: '' }
    const a = docFor(att, c.id, day)
    return { course: ref(c), status: a ? normalizeStatus(a.marks[studentId]) ?? 'present' : null, note: a?.notes?.[studentId] ?? '' }
  }
  return ([0, 1, 2, 3] as NtDay[]).map((day) => ({ day, AM: cell('AM', day), PM: cell('PM', day) }))
}

/* ---------------- a grade, the school ---------------- */

export interface StudentTotals { studentId: string; name: string; grade: number; absent: number; tardyExcused: number; tardyUnexcused: number; recorded: number }

/** Every rostered student's totals across the week, most absences first. */
export function studentTotals(courses: NtCourse[], rosters: NtRoster[], att: NtAttendance[], artifactOf?: ArtifactOf): StudentTotals[] {
  const t = new Map<string, StudentTotals>()
  for (const c of live(courses)) {
    const roster = rosters.find((r) => r.courseId === c.id)
    for (const st of roster?.students ?? []) {
      if (!t.has(st.studentId)) t.set(st.studentId, { studentId: st.studentId, name: st.name, grade: st.grade, absent: 0, tardyExcused: 0, tardyUnexcused: 0, recorded: 0 })
      const row = t.get(st.studentId)!
      for (const a of att.filter((x) => x.courseId === c.id)) {
        if (a.day === FRIDAY && artifactOf && artifactOf.get(st.studentId) !== c.block) continue
        const s = normalizeStatus(a.marks[st.studentId]) ?? 'present'
        row.recorded++
        if (s === 'absent') row.absent++
        if (s === 'tardy-excused') row.tardyExcused++
        if (s === 'tardy-unexcused') row.tardyUnexcused++
      }
    }
  }
  return [...t.values()].sort((a, b) => b.absent - a.absent || (b.tardyUnexcused + b.tardyExcused) - (a.tardyUnexcused + a.tardyExcused) || a.name.localeCompare(b.name))
}

export interface SessionRate { day: NtDay; block: NtBlock; recorded: number; present: number; absent: number; tardy: number; coursesTaken: number; coursesTotal: number }

/** The school by day and block — how much is in, and what it says. */
export function schoolRates(courses: NtCourse[], rosters: NtRoster[], att: NtAttendance[], grade?: number, artifactOf?: ArtifactOf): SessionRate[] {
  const out: SessionRate[] = []
  for (const day of [0, 1, 2, 3] as NtDay[]) {
    for (const block of ['AM', 'PM'] as NtBlock[]) {
      const inBlock = live(courses).filter((c) => c.block === block
        && expected(c, rosters.find((r) => r.courseId === c.id), day, artifactOf).length > 0)
      const r: SessionRate = { day, block, recorded: 0, present: 0, absent: 0, tardy: 0, coursesTaken: 0, coursesTotal: inBlock.length }
      for (const c of inBlock) {
        const a = docFor(att, c.id, day)
        if (!a) continue
        r.coursesTaken++
        for (const st of expected(c, rosters.find((x) => x.courseId === c.id), day, artifactOf)) {
          if (grade !== undefined && st.grade !== grade) continue
          const s = normalizeStatus(a.marks[st.studentId]) ?? 'present'
          r.recorded++
          if (s === 'present') r.present++
          else if (s === 'absent') r.absent++
          else r.tardy++
        }
      }
      out.push(r)
    }
  }
  return out
}
